import { cloneElement, useEffect, useRef, useState } from 'react';
import './stable-panels.css';

// All panels reserve their natural dimensions. Home can opt into an exit/enter
// sequence while other templates keep immediate selection and stable geometry.
export const StablePanels = ({ items, activeIndex, renderPanel, className = '', sequential = false, ...props }) => {
  const [visualIndex, setVisualIndex] = useState(activeIndex);
  const [phase, setPhase] = useState('idle');
  const [reducedMotion, setReducedMotion] = useState(() => typeof window !== 'undefined' && Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches));
  const visualRef = useRef(activeIndex);
  useEffect(() => {
    if (!sequential || !window.matchMedia) return undefined;
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const sync = () => setReducedMotion(query.matches);
    sync(); query.addEventListener?.('change', sync);
    return () => query.removeEventListener?.('change', sync);
  }, [sequential]);
  useEffect(() => {
    if (!sequential || reducedMotion || activeIndex === visualRef.current) {
      visualRef.current = activeIndex; setVisualIndex(activeIndex); setPhase('idle');
      return undefined;
    }
    let entryTimer, frame;
    const request = window.requestAnimationFrame || (callback => window.setTimeout(callback, 16));
    const cancel = window.cancelAnimationFrame || window.clearTimeout;
    setPhase('exiting');
    const exitTimer = window.setTimeout(() => {
      visualRef.current = activeIndex; setVisualIndex(activeIndex); setPhase('enter-start');
      frame = request(() => { frame = request(() => setPhase('entering')); });
      entryTimer = window.setTimeout(() => setPhase('idle'), 680);
    }, 360);
    return () => { window.clearTimeout(exitTimer); window.clearTimeout(entryTimer); if (frame !== undefined) cancel(frame); };
  }, [activeIndex, sequential, reducedMotion]);
  const displayed = sequential && !reducedMotion ? visualIndex : activeIndex;
  return <div className={`stable-panels ${className}`} data-panel-sequential={sequential ? 'true' : undefined} data-panel-phase={sequential ? phase : undefined} aria-busy={sequential && phase !== 'idle' ? true : undefined} {...props}>
    {items.map((item, index) => {
      const selected = index === displayed;
      const panel = renderPanel(item, index, selected);
      return cloneElement(panel, {
        key: item.code || item.key || item.id || index,
        'data-panel-active': selected ? 'true' : 'false',
        'aria-hidden': selected ? undefined : true,
        inert: selected ? undefined : true,
      });
    })}
  </div>;
};

export const selectPanelByKey = (event, index, count, select) => {
  const direction = ['ArrowDown', 'ArrowRight'].includes(event.key) ? 1 : ['ArrowUp', 'ArrowLeft'].includes(event.key) ? -1 : 0;
  const next = event.key === 'Home' ? 0 : event.key === 'End' ? count - 1 : direction ? (index + direction + count) % count : null;
  if (next === null) return;
  event.preventDefault();
  select(next);
  event.currentTarget.closest('[role="tablist"], [role="listbox"]')?.querySelectorAll('[role="tab"], [role="option"]')[next]?.focus({ preventScroll: true });
};